// LIBRARY IMPORTS
import React, { useState, useEffect, useContext, FC, useMemo, useCallback, useReducer } from 'react';
import { withStyles, WithStyles, CircularProgress, Hidden, MenuItem, Card, CardContent, Button, Dialog, DialogContent, DialogTitle, Typography, DialogActions } from '@material-ui/core';
import { RouteComponentProps } from 'react-router';
import { mdiLaptop } from '@mdi/js';
import InfiniteScroll from 'react-infinite-scroller';
import IoC from 'contexts/ioc.context';


// LOCAL IMPORTS
import { IFilterDataFlex } from 'gateways/recibo.interfaces';
import { LiteralsContext } from 'containers/shared/literals';
import { handleInfoDispatch } from 'utils/info-dispatch-mvl';
import { MoreInfoContext } from 'contexts/more_info.context';
import { FilterDataContext } from './filters/filter.data.context';
import FiltersReducer, { IFiltersState, QueryRequest } from './filters/filters.reducer';
import { FiltersContext } from './filters/filters.context';
import FiltersActive from './filters/filters.active';
import FiltersDialog from './filters/filters.dialog';
import { withRouter } from 'react-router-dom';
import { RecibosFlexiblesGateway } from 'gateways/recibo-flexibles.gateway';
import image from '../../../resources/no-deuda.png';
import NoContent from 'components/no-content';
import usePage from '../../../hooks/page.hook';
import TabPanel from "components/tab-panel";
import PanelDrawer from './filters/panel.drawer';
import ReciboCard from './recibo-flexible.card';
import Term from 'components/term';


// STYLES
import styles from '../../shared/tributos.styles';
import { IReciboFlexible } from 'gateways/recibo-flexibles.interfaces ';

const numberElementsPerPage = 50;

interface IParams {
    estado: 'pendiente' | 'pagado';
}

type Props = WithStyles<typeof styles> & RouteComponentProps<IParams>;

const PagoFlexible: FC<Props> = ({ classes }) => {


    // HOOKS
    const [, pageDispatcher] = usePage();
    const [, infoDispatch] = useContext(MoreInfoContext);

    // CONTEXT & GATEWAYS
    const terms = useContext(LiteralsContext);
    const ioc = useContext(IoC);
    const recibosFlexiblesGateway = useMemo(() => ioc.get(RecibosFlexiblesGateway) as RecibosFlexiblesGateway, [ioc]);

    // STATES
    const [loadingRecibos, setLoadingRecibos] = useState(false);
    const [page, setPage] = useState(0);
    const [, setOpenSubMenuAnchorEl] = React.useState<HTMLElement | null>(null);
    const [tabActive, setTabActive] = useState(0);

    // DATA
    const [recibos, setRecibos] = useState<IReciboFlexible[]>([]);

    // COUNTS
    const [recibosCount, setRecibosCount] = useState(0);

    // FILTERS
    const filtersReducer = useReducer(FiltersReducer, {});
    const [filters, filtersDispatch] = filtersReducer;
    const [filterData, setFilterData] = useState<IFilterDataFlex>({});
    const [showFilters, setShowFilters] = useState(false);

    // MEMOS
    const hasMore = useMemo(() => recibosCount > (page === 0 ? (page + 1) : page) * numberElementsPerPage, [page, recibosCount]);
    const noContent = useMemo(() => (!loadingRecibos && recibos.length === 0) || (recibosCount === 0 && recibos.length > 0), [loadingRecibos, recibos.length, recibosCount]);

    // STATES PAGO A CUENTA
    const [showModalAvisoPagoACuenta, setShowModalAvisoPagoACuenta] = useState(false);

    // HANDLERS

    const handleModalAvisoPagoACuenta = () => {
        setShowModalAvisoPagoACuenta(!showModalAvisoPagoACuenta);
    }

    const buildQueryRequest = useCallback((newFilters: IFiltersState, newPage: number) => {
        return Object.assign({}, newFilters, {

            skip: newPage * numberElementsPerPage,
            take: numberElementsPerPage
        }) as QueryRequest;
    }, []);

    const handleCloseSubMenu = useCallback(() => setOpenSubMenuAnchorEl(null), []);

    const handleShowFilters = useCallback(() => {
        setShowFilters(true);
        handleCloseSubMenu();
    }, [handleCloseSubMenu]);

    const handleHideFilters = useCallback(() => setShowFilters(false), []);

    const handleLoadRecibos = useCallback(async (newFilters: IFiltersState, newPage: number) => {
        const query = buildQueryRequest(newFilters, newPage);

        if (!loadingRecibos) {

            setLoadingRecibos(true);

            try {
                const recibosReceived = await recibosFlexiblesGateway.findAll(query);
                if (recibosReceived) {
                    setRecibos(newPage === 0 ? recibosReceived : recibos.concat(recibosReceived));
                }
            } catch (error) {
                pageDispatcher({
                    type: 'show-notification',
                    payload: {
                        message: (error as any).message,
                        variant: 'error',
                    }
                });
            } finally {
                setLoadingRecibos(false);
            }
        }

    }, [buildQueryRequest, loadingRecibos, pageDispatcher, recibosFlexiblesGateway, recibos]);

    const handleLoadMoreRecibos = useCallback(() => {
        if (hasMore && !loadingRecibos) {
            setPage(page + 1);
            handleLoadRecibos(filters, page + 1);
        }
    }, [hasMore, loadingRecibos, page, handleLoadRecibos, filters]);


    const handleExportRequest = useCallback(async () => {

    }, []);

    const handleFiltersChanged = useCallback((newFilters: IFiltersState) => {
        setPage(0);
        handleLoadRecibos(newFilters, 0);
    }, [handleLoadRecibos]);



    const handleShowMasInfo = useCallback((templateName: string) => {
        handleInfoDispatch(infoDispatch, 'show-info', templateName)
    }, [infoDispatch])

    // USE EFFECTS


    useEffect(() => {
        pageDispatcher({
            type: 'setHeader',
            header: {
                icon: mdiLaptop,
                title: <Term component="CarteroVirtual" text={'Pago flexible'} />,
                moreInfoTemplate: 'Pago_flexible',
                right: (
                    <>
                        <MenuItem button onClick={handleShowFilters}><Term component="Global" text="Filtrar resultados" /></MenuItem>
                        <MenuItem button onClick={() => handleShowMasInfo('Pago_flexible')}>
                            <Term component="Global" text="Mas informacion" />
                        </MenuItem>
                    </>
                )
            },
            menu: true,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageDispatcher, terms, recibos]);



    useEffect(() => {
        (async () => {
            handleFiltersChanged(filters);
            const query = buildQueryRequest(filters, 0);
            delete query.skip;
            delete query.take;
            {
                setRecibosCount(0);
                const countResult = await recibosFlexiblesGateway.getCount(query);
                setRecibosCount(countResult ? countResult : 0);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    useEffect(() => {
        setRecibos([]);
        setPage(0);
        setTabActive(0);
        const query = buildQueryRequest(filters, 0);
        delete (query as any).skip;
        delete (query as any).take;
        const asyncEffect = async () => {
                const filterDataTemp = await recibosFlexiblesGateway.getFilterData(query);
                if (filterDataTemp) {
                    setFilterData(filterDataTemp);
                }
            }
        asyncEffect()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <FiltersContext.Provider value={filtersReducer}>
                <FilterDataContext.Provider value={filterData}>
                    <div className={classes.root}>
                        <div className={classes.recibosContainer}>
                            <FiltersActive
                                onChange={handleFiltersChanged}
                            />

                            {/* TABS */}

                            {/* {pendiente ?
                                    null :
                                    <div style={{ display: 'flex', flexDirection: 'row', width: '100%', marginBottom: '1%', backgroundColor: 'white', borderRadius: '3px' }}>
                                        <CustomTabs
                                            labelTab1={translate('CarteroVirtual', 'tributos_locales', terms)}
                                            labelTab2={translate('CarteroVirtual', 'tributos_autonomicos', terms)}
                                            onChange={handleChange}
                                            tabActive={tabActive}
                                        />
                                    </div>
                                } */}
                            <TabPanel value={tabActive} index={0}>
                                <NoContent
                                    image={image}
                                    visible={noContent}
                                    component='CarteroVirtual'
                                    text={
                                        'No se ha encontrado deuda pendiente'
                                    }
                                />

                                {
                                    recibosCount > 0
                                        ?
                                        <>
                                            <Card className={classes.cardContador}>
                                                <CardContent className={classes.cardContadorContainer} >
                                                    <span className={classes.titleCardContador}>
                                                        <Term component="CarteroVirtual" text={'TituloCarteroVirualPagoFlexible'} params={[recibosCount]} />
                                                    </span>
                                                </CardContent>
                                            </Card>
                                            <InfiniteScroll
                                                pageStart={0}
                                                initialLoad={false}
                                                loadMore={handleLoadMoreRecibos}
                                                hasMore={hasMore}
                                                loader={<div key="infiniteProgress" style={{ display: hasMore ? 'block' : 'none' }} className={classes.centerContent}><CircularProgress className={classes.progress} /></div>}
                                            >
                                                {recibos.map((recibo: IReciboFlexible, index: number) => (
                                                    <ReciboCard
                                                        key={'Recibo_' + index}
                                                        recibo={recibo}
                                                    />
                                                ))}
                                            </InfiniteScroll>
                                        </>
                                        :
                                        noContent ?
                                        null:
                                        <div key="progress" className={classes.centerContent}><CircularProgress className={classes.progress} /></div>
                                }
                            </TabPanel>

                        </div>



                    </div>

                    <div className={classes.drawer}>
                        <Hidden mdUp implementation="css">
                            <FiltersDialog
                                open={showFilters}
                                onClose={handleHideFilters}
                                onChange={handleFiltersChanged}
                                onExportRequest={handleExportRequest}
                            />
                        </Hidden>

                        <Hidden smDown implementation="css">
                            <PanelDrawer
                                onFiltersChange={handleFiltersChanged}
                                onExportRequest={handleExportRequest}
                            />
                        </Hidden>
                    </div>
                </FilterDataContext.Provider>
            </FiltersContext.Provider >

            {/* MODAL AVISO PAGO CUENTA */}

            <Dialog open={showModalAvisoPagoACuenta}>
                <div style={{ padding: 20 }}>
                    <DialogTitle>
                        <Typography style={{ color: '#154b60', fontSize: 30, fontWeight: 100 }}>
                            <Term component='Global' text='Informacion' />
                        </Typography>
                    </DialogTitle>

                    <DialogActions>
                        <Button onClick={handleModalAvisoPagoACuenta} color="primary">
                            <Term component='Global' text='Cerrar' />
                        </Button>
                    </DialogActions>
                </div>
            </Dialog>
        </>
    );
};

export default withRouter(withStyles(styles)(PagoFlexible));